interface TeamDeletedOptions {
  deletedTeamId: string
  activeTeamId: string | null
  teams: readonly { id: string }[]
  /** Renews the access token so its groups claim drops the deleted team. */
  refreshToken: () => Promise<void>
  /** Reloads the page on the given team. */
  switchTeam: (teamId: string) => void
  refetchTeams: () => void
}

/**
 * What happens once the api has deleted a team. The token still lists the
 * team until it is renewed, and both the team list and the api read teams
 * from the token, so the refresh comes first. Deleting the active team
 * moves the user to another one, which reloads the page.
 *
 * The team is already gone when this runs, so a refresh that fails is
 * logged and the flow goes on: the list reloads from the token the tab
 * still holds, and the next renewal (automatic, or on return to the tab)
 * brings the groups claim up to date. A dead session shows up as a 401 on
 * the next request, which AuthContext already handles.
 */
export async function afterTeamDeleted({
  deletedTeamId,
  activeTeamId,
  teams,
  refreshToken,
  switchTeam,
  refetchTeams,
}: TeamDeletedOptions): Promise<'switched' | 'refetched'> {
  try {
    await refreshToken()
  } catch (error: unknown) {
    console.warn('Token refresh after deleting a team failed:', error)
  }
  if (deletedTeamId === activeTeamId) {
    const next = teams.find((t) => t.id !== deletedTeamId)
    if (next) {
      switchTeam(next.id)
      return 'switched'
    }
  }
  refetchTeams()
  return 'refetched'
}
